"use client";

import { createContext, useContext, useMemo, ReactNode } from "react";
import { useRealtimeMarketPrice, type MarketLike } from "@/hooks/useRealtimeMarketPrice";

interface MarketPriceData {
  // Live prices based on lastTradePrice (for % CHANCE display)
  livePrices: number[];
  // Buy prices based on orderbook (for buy buttons)
  buyPrices: {
    buyYes: number;
    buyNo: number;
  };
  // Connection status
  isConnected: boolean;
  // Whether realtime updates are enabled
  isRealtime: boolean;
}

const MarketPriceContext = createContext<MarketPriceData | null>(null);

interface MarketPriceProviderProps {
  children: ReactNode;
  clobTokenIds: string[];
  initialPrices: number[];
  realtimeUpdates?: boolean;
}

/**
 * MarketPriceProvider — thin wrapper around useRealtimeMarketPrice.
 *
 * Delegates ALL price logic (WS, snapshot polling, getBuyPrices) to the
 * central hook so every component sees the same prices. No duplicate
 * subscriptions, no duplicate polling, no duplicate calculations.
 */
export function MarketPriceProvider({
  children,
  clobTokenIds,
  initialPrices,
  realtimeUpdates = false,
}: MarketPriceProviderProps) {
  // Build a MarketLike object for the central hook.
  //
  // Critical: `initialPrices` is always [buyYes, buyNo] (event grid already
  // resolved to bestAsk/bestBid via getLiveBuyPrices). We surface those as
  // `bestAsk` / `bestBid` so the hook treats them as Gamma-quality defaults
  // before any WS update arrives. Without this, the MarketLike has no
  // lastTradePrice/bestAsk and getMarketPrice() latches onto WS
  // lastTradePrice — which is the "chance" price, NOT the buy price. That
  // caused the sidebar trading panel to show 37¢ (last trade) while the
  // grid showed 49¢ (bestAsk) for the same market.
  const markets: MarketLike[] = useMemo(() => {
    const buyYes = initialPrices?.[0];
    const buyNo = initialPrices?.[1];
    const bestAsk = typeof buyYes === 'number' && buyYes > 0 && buyYes < 1 ? buyYes : undefined;
    const bestBid = typeof buyNo === 'number' && buyNo > 0 && buyNo < 1 ? 1 - buyNo : undefined;
    return [{
      clobTokenIds,
      outcomePrices: initialPrices,
      bestAsk,
      bestBid,
    }];
  }, [clobTokenIds, initialPrices]);

  const {
    getMarketPrice,
    getBuyPrices,
    isRealtime,
  } = useRealtimeMarketPrice({
    markets,
    enabled: realtimeUpdates,
  });

  const market = markets[0];
  const livePrices = useMemo(() => {
    const [yes, no] = getMarketPrice(market);
    return [yes, no];
  }, [getMarketPrice, market]);

  const buyPrices = useMemo(() => {
    const { buyYes, buyNo } = getBuyPrices(market);
    return { buyYes, buyNo };
  }, [getBuyPrices, market]);

  const value = useMemo(
    () => ({
      livePrices,
      buyPrices,
      isConnected: isRealtime,
      isRealtime: realtimeUpdates,
    }),
    [livePrices, buyPrices, isRealtime, realtimeUpdates]
  );

  return (
    <MarketPriceContext.Provider value={value}>
      {children}
    </MarketPriceContext.Provider>
  );
}

export function useMarketPrice(): MarketPriceData {
  const context = useContext(MarketPriceContext);
  if (!context) {
    throw new Error("useMarketPrice must be used within a MarketPriceProvider");
  }
  return context;
}

// Optional hook that returns null if not in provider (for components that can work without it)
export function useMarketPriceOptional(): MarketPriceData | null {
  return useContext(MarketPriceContext);
}
